// services/school-ui/src/pages/chat/attachments/HttpAgentWithContext.ts
//
// HttpAgent subclass that injects forwardedProps.attachment_ids into
// every AG-UI run input. The list is derived from the agent's most-
// recent user message — any complete attachment part on it is
// resolved (via the ref registry, populated by the adapter on upload
// and by history-load) to recover its kernel-assigned id, then the
// id is added to a flat string[] under forwardedProps.attachment_ids.
//
// Wire shape pinned at nova-os#455:
//   forwardedProps: { attachment_ids: ["att-1", "att-2", ...] }
//
// The kernel's chat handler (cf. nova-os internal/server/routers/
// chat.go) lifts forwardedProps.attachment_ids into
// metadata.attachment_ids, then runs validateAttachmentRefs against
// the store — auth-checks each id against the conversation + the
// agent's `accepts_attachments` opt-in.
//
// Why subclass instead of a runtime middleware: @assistant-ui/react-
// ag-ui at v0.0.30 doesn't expose a context-mutation hook on its
// public API. Subclassing HttpAgent is the smallest stable surface
// that gives us the injection point and survives library minor bumps.

import { HttpAgent } from "@ag-ui/client";
import { type RunAgentInput } from "@ag-ui/core";
import { lookupRef } from "./refRegistry";
import {
  handleNovaEvent,
  progressRunFinished,
  progressRunStarted,
  progressSetConversation,
} from "../progressStore";

type ExtraForwardedProps = Record<string, unknown>;
type HttpAgentOptions = ConstructorParameters<typeof HttpAgent>[0];

interface HttpAgentWithContextOptions extends HttpAgentOptions {
  forwardedProps?: ExtraForwardedProps;
}

interface ThreadUserPartImage {
  type: "image";
  image: string;
}
interface ThreadUserPartFile {
  type: "file";
  mimeType: string;
  data: string;
}
type ThreadUserPart =
  | ThreadUserPartImage
  | ThreadUserPartFile
  | { type: "text"; text: string };

/** Recover the kernel-assigned attachment id from a ThreadUserMessage
 *  part. The part itself carries the download_url (under `image` for
 *  images, `data` for files); the registry — populated by the adapter
 *  on upload and by history-load — supplies the matching ref's id.
 *
 *  Returns null when the part isn't an attachment (text), OR when the
 *  registry has no entry AND the url doesn't follow the canonical
 *  /v1/conversations/{cid}/attachments/{aid}/download path that the
 *  url-derivation fallback expects. */
function partToAttachmentID(part: ThreadUserPart): string | null {
  if (part.type === "text") return null;

  // The download_url lives under `image` (images) or `data` (files) in the
  // assistant-ui ThreadUserMessagePart shape, but by the time this runs
  // over RunAgentInput.messages the react-ag-ui adapter may have reshaped
  // the part — so extract liberally across the plausible keys and NEVER
  // assume a string. A part with type image/file but a non-string url used
  // to crash the whole run at idFromDownloadURL(undefined).match(...)
  // ("Cannot read properties of undefined (reading 'match')"), which the
  // pre-fix double-chip bug hid by keeping the send button disabled.
  const p = part as unknown as Record<string, unknown>;
  // The react-ag-ui adapter reshapes attachment parts to
  // {type, source:{type:"url", value:<download_url>, mimeType}} — the URL
  // is under source.value. The plain assistant-ui shapes (image/data) are
  // kept for history-loaded / direct-shape parts.
  const url = firstString(
    (p.source as Record<string, unknown> | undefined)?.value,
    p.image,
    p.data,
    p.url,
    (p.image as Record<string, unknown> | undefined)?.url,
    (p.file as Record<string, unknown> | undefined)?.url,
    (p.source as Record<string, unknown> | undefined)?.url,
  );
  if (!url) {
    // Diagnostic: surfaces the real part shape so we can widen the key
    // list if an attachment ever fails to resolve. Cheap (one warn per
    // unresolved attachment part) and only fires when url extraction
    // missed — never on the happy path.
    try {
      // eslint-disable-next-line no-console
      console.warn("[attachments] unresolved part shape:", JSON.stringify(part));
    } catch {
      /* ignore */
    }
    return null;
  }

  // Primary: registry lookup (adapter populated it on upload success).
  const full = lookupRef(url);
  if (full) return full.id;

  // Fallback: parse the id from the canonical url path
  // /v1/conversations/{cid}/attachments/{aid}/download. Defensive
  // against edge cases (cross-tab reload, registry miss).
  return idFromDownloadURL(url);
}

function firstString(...vals: unknown[]): string | undefined {
  for (const v of vals) if (typeof v === "string" && v) return v;
  return undefined;
}

function idFromDownloadURL(url: string): string | null {
  if (typeof url !== "string") return null;
  const m = url.match(/\/attachments\/([^/]+)(?:\/download)?(?:[?#].*)?$/);
  if (m && m[1]) return decodeURIComponent(m[1]);
  return null;
}

export class HttpAgentWithContext extends HttpAgent {
  private readonly forwardedProps: ExtraForwardedProps;

  constructor({ forwardedProps, ...options }: HttpAgentWithContextOptions) {
    super(options);
    this.forwardedProps = forwardedProps || {};

    // threadId IS the conversation id (AssistantUiThread pins it). Progress
    // is stored per conversation (#24): pointing the panel here is
    // idempotent for the same id, so the agent reconstructions that happen
    // mid-run (auth-header rotation on token refresh) no longer wipe live
    // progress, while switching conversations shows that conversation's own
    // (possibly empty) progress. "" groups the brand-new-chat placeholder.
    const convId = options.threadId ?? "";
    progressSetConversation(convId);

    // Tap the run's AG-UI CUSTOM progress events (nova.phase / nova.plan /
    // nova.step / nova.skill_*) into the progress store. assistant-ui's
    // aggregator drops CUSTOM events from ThreadState, so LiveAgentActivity
    // can't read them from the runtime — subscribe() on the agent is the
    // public, library-stable hook that receives every event for every run.
    this.subscribe({
      onRunInitialized: () => {
        progressRunStarted(convId);
      },
      onRunFinalized: () => {
        progressRunFinished(convId);
      },
      onRunFailed: () => {
        progressRunFinished(convId);
      },
      onCustomEvent: ({ event }) => {
        const name = (event as { name?: unknown })?.name;
        if (typeof name === "string" && name.startsWith("nova.")) {
          handleNovaEvent(convId, name, (event as { value?: unknown }).value);
        }
      },
    });
  }

  // react-ag-ui calls runAgent with a full RunAgentInput as the first
  // argument (see AgUiThreadRuntimeCore.js). We intercept here to merge
  // forwardedProps.attachment_ids before delegating to the parent.
  // forwardedProps is typed `any` in RunAgentInput and is the AG-UI-
  // blessed carrier for custom kernel-facing data.
  override runAgent(
    input?: RunAgentInput & { abortController?: AbortController },
    subscriber?: Parameters<HttpAgent["runAgent"]>[1],
  ): Promise<Awaited<ReturnType<HttpAgent["runAgent"]>>> {
    if (!input) return super.runAgent(input, subscriber);

    const messages = input.messages ?? [];
    const lastUser = [...messages]
      .reverse()
      .find((m) => m.role === "user") as
      | { role: "user"; content: ThreadUserPart[] | string }
      | undefined;

    let attachmentIDs: string[] = [];
    if (lastUser && Array.isArray(lastUser.content)) {
      attachmentIDs = lastUser.content
        .map((p) => partToAttachmentID(p as ThreadUserPart))
        .filter((x): x is string => x !== null);
    }

    const merged: typeof input = {
      ...input,
      forwardedProps: {
        ...(input.forwardedProps ?? {}),
        ...this.forwardedProps,
        ...(attachmentIDs.length > 0
          ? { attachment_ids: attachmentIDs }
          : {}),
      },
    };

    return super.runAgent(merged, subscriber);
  }
}
